import { cloneDeep, isEqual } from "lodash";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tooltip } from "primereact/tooltip";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { v4 as uuid } from "uuid";
import { useClientId } from "../../../ClientConfig";
import { IfFeatureEnabled } from "../../../IfFeatureEnabled";
import * as client from "../../../client";
import { ClientId, featureEnabled, perClient } from "../../../client-features";
import { ThemeContext } from "../../../themes/Theme";
import {
  applyOverridesToAddress,
  applyOverridesToCompanyInfo,
} from "../Conversion";
import { formatAbbreviatedMoney } from "../Money";
import * as OL from "../OverrideableList";
import { flaggedHighlightColor } from "../dashboard/util";
import { getSelectablePolicyFormNames } from "../utils";
import { useUpdateReport } from "../data";
import { EnumEditor } from "../sov/CellEditors";
import styles from "./GeneralInput.module.css";
import { BUSINESS_DESCRIPTIONS } from "./business-descriptions";
import { Calendar } from "primereact/calendar";
import { dateToMMDDYYYY, mmddyyyyToDate } from "../../../date-utils";
import { Dropdown } from "primereact/dropdown";
import { perilSentenceFromEnabledPerils } from "./util";
import {
  BrokerCommissionInput,
  FormSelectionDropdown,
  PerilCheckboxes,
} from "../policy-terms/PolicyTermControls";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";

const REQUIRED_FIELDS = perClient<(keyof client.RawCompanyInfo)[]>({
  luminor: [],
});

type ValuationType = NonNullable<
  client.ValuationInfo[keyof client.ValuationInfo]
>;

// Valuation type options per group, matching the Policy Fly proto enums
const PHYSICAL_VALUATION_TYPE_1_OPTIONS: ValuationType[] = [
  "Excluded",
  "Actual Cash Value",
  "Replacement Cost",
  "Functional Replacement Cost",
] as const;

const PHYSICAL_VALUATION_TYPE_2_OPTIONS: ValuationType[] = [
  "Excluded",
  "Actual Cash Value",
  "Replacement Cost",
  "Selling Price",
] as const;

const BI_VALUATION_TYPE_OPTIONS: ValuationType[] = [
  "Excluded",
  "Actual Loss Sustained",
] as const;

const EQUIPMENT_VALUATION_TYPE_OPTIONS: ValuationType[] = [
  "Excluded",
  "Actual Cash Value",
  "Replacement Cost",
  "Agreed Value",
  "Actual Cash Value, except Replacement cost if newer than 5 years old",
] as const;

// "pinned" is set by a backend backfill on reports modeled before wind+surge
// support; it is display-only and never user-selectable.
const SURGE_ASSOCIATION_OPTIONS: {
  label: string;
  value: NonNullable<client.PolicyInfo["surge_association"]>;
}[] = [
  { label: "Wind & Surge", value: "wind" },
  { label: "Flood & Surge", value: "flood" },
  { label: "Surge Not Included", value: "none" },
  { label: "Wind Only (Pinned)", value: "pinned" },
];

// The backend's default split, read from the OpenAPI schema; shown (and saved)
// for reports created before reinsurer percentages were stored on the report.
const DEFAULT_REINSURER_INFO: client.ReinsurerInfo = {
  ren_percentage: client.$ReinsurerInfo.properties.ren_percentage.default,
  nephila_percentage:
    client.$ReinsurerInfo.properties.nephila_percentage.default,
  starr_percentage: client.$ReinsurerInfo.properties.starr_percentage.default,
};

const ReinsurerPercentageField = ({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) => (
  <div className={styles.horizontalContainer}>
    <span className={styles.label}>{label}</span>
    <InputNumber
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.value ?? 0)}
      suffix="%"
      minFractionDigits={0}
      maxFractionDigits={2}
      min={0}
      max={100}
      style={{ maxWidth: "160px" }}
    />
  </div>
);

// Sentinel option that puts the field into freeform mode. Real valuation
// values can never collide with it since it is not valid prose.
const CUSTOM_VALUATION_SENTINEL = "__custom__";

// A valuation type field: a dropdown of known values plus a "Custom…" entry
// that reveals a text input for any freeform valuation language. A value that
// isn't one of the field's known options is treated as custom on load.
const ValuationTypeField = ({
  label,
  options,
  value,
  disabled,
  onChange,
}: {
  label: string;
  options: ValuationType[];
  value: string | null | undefined;
  disabled: boolean;
  onChange: (value: string | null) => void;
}) => {
  const isCustomValue =
    value != null && value !== "" && !options.includes(value as ValuationType);
  const [isCustom, setIsCustom] = useState(isCustomValue);

  const dropdownOptions = [
    ...options.map((option) => ({ label: option, value: option })),
    { label: "Custom…", value: CUSTOM_VALUATION_SENTINEL },
  ];

  return (
    <div className={styles.horizontalContainer}>
      <span className={styles.label}>{label}</span>
      <div className={styles.valuationControls}>
        <Dropdown
          disabled={disabled}
          options={dropdownOptions}
          value={isCustom ? CUSTOM_VALUATION_SENTINEL : (value ?? null)}
          onChange={(e) => {
            if (e.value === CUSTOM_VALUATION_SENTINEL) {
              setIsCustom(true);
              onChange(null);
            } else {
              setIsCustom(false);
              onChange(e.value ?? null);
            }
          }}
          style={{ width: "240px" }}
          scrollHeight="400"
        />
        {isCustom && (
          <InputText
            disabled={disabled}
            value={value ?? ""}
            placeholder="Enter custom valuation type…"
            onChange={(e) => onChange(e.target.value || null)}
            style={{ width: "240px" }}
          />
        )}
      </div>
    </div>
  );
};

type FlagIds =
  | client.CompanyInfo["private_flags"][string][number]
  | "no_location_in_mailing_state";

const flagTextForCompanyInfoFlag = (
  companyInfoFlag: FlagIds,
  safeOriginalValue: string
) => {
  if (typeof companyInfoFlag === "string") {
    switch (companyInfoFlag) {
      case "low_confidence":
        return "We have low confidence in this value";
      case "medium_confidence":
        return "We have medium confidence in this value";
      case "modified_by_sanitization":
        return `The original value was: "${safeOriginalValue}"`;
      case "could_not_sanitize":
        return `We failed to find a reasonable output value for original: "${safeOriginalValue}". We used the default value instead.`;
      case "default_used":
        return "We failed to find a matching value, so used a default instead.";
      case "po_box_insured_address":
        return "This address may be a PO Box";
      case "no_location_in_mailing_state":
        return "There is no location in the SOV in this state";
    }
  }

  switch (companyInfoFlag.flag_id) {
    case "year_incorporated_derived_from_search":
      return (
        "The year incorporated was found via a web search. Our primary source was " +
        companyInfoFlag.source
      );
    case "multiple_company_names":
      return `Multiple company names were found: ${companyInfoFlag.company_names
        .map((name) => `"${name}"`)
        .join(", ")}`;
  }
};

const flagsForCompanyInfoField = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo,
  safeCompanyInfo: client.RawCompanyInfo,
  clientId: ClientId,
  states: string[]
) => {
  const flags: FlagIds[] = companyInfo.private_flags[field] ?? [];
  if (
    featureEnabled(clientId, "flag-no-location-in-insured-mailing-state") &&
    field === "company_state" &&
    safeCompanyInfo.company_state &&
    !states.includes(safeCompanyInfo.company_state)
  ) {
    flags.push("no_location_in_mailing_state");
  }

  return flags;
};

const flagTextForCompanyInfoField = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo,
  safeCompanyInfo: client.RawCompanyInfo,
  clientId: ClientId,
  states: string[]
) => {
  const privateFlags = flagsForCompanyInfoField(
    companyInfo,
    field,
    safeCompanyInfo,
    clientId,
    states
  );
  const allFlagTexts = [];
  for (const flag of privateFlags) {
    const originalValue = companyInfo.private_original_values[field] ?? "None";
    allFlagTexts.push(
      flagTextForCompanyInfoFlag(
        flag,
        typeof originalValue === "string" ? originalValue : ""
      )
    );
  }

  if (allFlagTexts.length === 0) {
    return "";
  }

  return allFlagTexts.map((text) => `- ${text}`).join("\n");
};

const severityForFlag = (companyInfoFlag: FlagIds): number => {
  if (typeof companyInfoFlag === "string") {
    switch (companyInfoFlag) {
      case "low_confidence":
        return 81;
      case "medium_confidence":
        return 65;
      case "modified_by_sanitization":
        return 0;
      case "could_not_sanitize":
        return 90;
      case "default_used":
        return 50;
      case "po_box_insured_address":
        return 90;
      case "no_location_in_mailing_state":
        return 90;
    }
  }

  switch (companyInfoFlag.flag_id) {
    case "year_incorporated_derived_from_search":
      return 65;
    case "multiple_company_names":
      return 65;
  }
};

const maxSeverityForCompanyInfoField = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo,
  safeCompanyInfo: client.RawCompanyInfo,
  clientId: ClientId,
  states: string[]
): number => {
  const severities = flagsForCompanyInfoField(
    companyInfo,
    field,
    safeCompanyInfo,
    clientId,
    states
  )
    .map((flag) => severityForFlag(flag))
    .concat(0);
  return Math.max(...severities);
};

const elementForFlags = (
  companyInfo: client.CompanyInfo,
  field: keyof client.RawCompanyInfo,
  safeCompanyInfo: client.RawCompanyInfo,
  clientId: ClientId,
  states: string[]
) => {
  const flags = flagsForCompanyInfoField(
    companyInfo,
    field,
    safeCompanyInfo,
    clientId,
    states
  );
  const yearIncorporationSearchFlag = flags.find(
    (flag) =>
      !(typeof flag === "string") &&
      flag.flag_id === "year_incorporated_derived_from_search"
  );
  if (yearIncorporationSearchFlag) {
    return (
      <Button
        icon="pi pi-external-link"
        size="small"
        style={{ height: "24px", width: "24px", padding: "0px" }}
        text
        severity="secondary"
        onClick={() => {
          if (
            !(typeof yearIncorporationSearchFlag === "string") &&
            yearIncorporationSearchFlag.flag_id ===
              "year_incorporated_derived_from_search"
          ) {
            window.open(yearIncorporationSearchFlag.source, "_blank");
          }
        }}
      />
    );
  }

  return <></>;
};

const requiredTextForField = (
  clientId: ClientId,
  field: keyof client.RawCompanyInfo
) => {
  if (REQUIRED_FIELDS[clientId]?.includes(field)) {
    return "This field is required";
  }
};

const flagStyles = ({
  hasOverride,
  maxSeverity,
  isLightMode,
  isRequiredAndIsBlank,
  isBlank,
}: {
  hasOverride: boolean;
  maxSeverity: number;
  isLightMode: boolean;
  isBlank: boolean;
  isRequiredAndIsBlank: boolean;
}) => {
  const inputTextStyles: React.CSSProperties = {
    height: "24px",
    width: "200px",
    boxShadow:
      hasOverride || maxSeverity || isRequiredAndIsBlank
        ? `0 0 0 2px ${flaggedHighlightColor(
            maxSeverity,
            isLightMode,
            hasOverride,
            isRequiredAndIsBlank
          )}`
        : "",
  };
  const labelStyles: React.CSSProperties =
    hasOverride || maxSeverity || isRequiredAndIsBlank
      ? {
          backgroundColor: flaggedHighlightColor(
            maxSeverity,
            isLightMode,
            hasOverride,
            isRequiredAndIsBlank
          ),
          padding: "2px",
          height: isBlank ? "23px" : "auto",
          minWidth: isBlank ? "44px" : "auto",
        }
      : {};
  return { labelStyles, inputTextStyles };
};

type AdditionalInterestFlagIds =
  client.OverrideableListElementRawAdditionalInterestLiteralTodo["flags"][string];

type InterestList = OL.OverrideableListElement<
  client.RawAdditionalInterest,
  AdditionalInterestFlagIds
>[];

const formattedAddress = (address: client.RawAddress): string => {
  const street = address.geocoded_street_address;
  const city = address.city;
  const state = address.state;
  const location = address.premises_number ?? "None";
  const building = address.building_number ?? "None";
  return `${street}, ${city} ${state} (Loc ${location}, Bldg ${building})`;
};

const EditableAdditionalInterests = ({
  additionalInterests,
  setAdditionalInterests,
  isEditing,
  allAddresses,
}: {
  additionalInterests: InterestList;
  setAdditionalInterests: Dispatch<SetStateAction<InterestList>>;
  isEditing: boolean;
  allAddresses: client.Address[];
}) => {
  return (
    <>
      {additionalInterests
        .map((element, index) => {
          const values = OL.effectiveValues(element);
          return values ? ([index, values, element] as const) : undefined;
        })
        .filter((v) => v !== undefined)
        .map(([index, values, element]) => {
          const onEdit = (
            field: keyof client.RawAdditionalInterest,
            value: string
          ) => {
            setAdditionalInterests((interests) => {
              return interests.map((a, i) =>
                i === index ? OL.overriden(a, { [field]: value }) : a
              );
            });
          };

          const onEditAddress = (value: string) => {
            setAdditionalInterests((interests) => {
              return interests.map((a, i) =>
                i === index ? OL.overriden(a, { linked_address_id: value }) : a
              );
            });
          };

          return (
            <Fragment key={index}>
              <EditableAIFlaggedField
                label="Entity Name"
                field="entity_name"
                isEditing={isEditing}
                listElement={element}
                values={values}
                onEdit={onEdit}
              />
              <EditableAIFlaggedField
                label="Entity Address"
                field="entity_address"
                isEditing={isEditing}
                listElement={element}
                values={values}
                onEdit={onEdit}
              />
              <EditableAIFlaggedField
                label="Interest type"
                field="form_name"
                isEditing={isEditing}
                listElement={element}
                values={values}
                onEdit={onEdit}
                enumValues={["LENDER'S LOSS PAYABLE", "LOSS PAYEE", "MORTAGEE"]}
              />
              <EditableAddressField
                label="Building Address"
                isEditing={isEditing}
                addresses={allAddresses}
                onEdit={onEditAddress}
                listElement={element}
              />
              {isEditing && (
                <div>
                  <Button
                    label="Remove"
                    size="small"
                    severity="danger"
                    raised
                    text
                    onClick={() => {
                      setAdditionalInterests((interests) =>
                        interests.map((val, i) =>
                          i === index ? OL.deleted(val) : val
                        )
                      );
                    }}
                  />
                </div>
              )}
              <Divider />
            </Fragment>
          );
        })}
      {isEditing && (
        <Button
          size="small"
          style={{ alignSelf: "start" }}
          text
          raised
          label="Add"
          onClick={() =>
            setAdditionalInterests((interests) =>
              interests.concat(
                OL.inserted<
                  client.RawAdditionalInterest,
                  AdditionalInterestFlagIds
                >({
                  entity_name: "",
                  entity_address: "",
                  form_name: "",
                  linked_address_id: null,
                })
              )
            )
          }
        />
      )}
    </>
  );
};

type EditableFlaggedFieldProps = {
  rawCompanyInfo: client.CompanyInfo;
  safeCompanyInfo: client.RawCompanyInfo;
  setSafeCompanyInfo: Dispatch<SetStateAction<client.RawCompanyInfo>>;
  field: keyof client.RawCompanyInfo;
  label: string;
  isEditing: boolean;
  states: string[];
  enumValues?: string[];
  placeholder?: string;
  isMonetary?: boolean;
  isDate?: boolean;
};

const EditableAddressField = ({
  listElement,
  addresses,
  label,
  isEditing,
  onEdit,
}: {
  listElement: OL.OverrideableListElement<
    client.RawAdditionalInterest,
    AdditionalInterestFlagIds
  >;
  addresses: client.Address[];
  label: string;
  isEditing: boolean;
  onEdit: (newVal: string) => void;
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const linkedAddressId = OL.effectiveValues(listElement)?.linked_address_id;
  const field = "linked_address_id";
  const formattedAddressToAddress = useMemo(
    () =>
      Object.fromEntries(
        addresses.map((a) => [formattedAddress(applyOverridesToAddress(a)), a])
      ),
    [addresses]
  );

  const hasOverride =
    listElement.overrides[field] !== undefined &&
    listElement.original !== null &&
    listElement.overrides[field] !== listElement.original[field];

  const tooltipId = `tooltip-${uuid()}`;
  const matchingAddress = addresses.find(
    (a) => a.address_id === linkedAddressId
  );
  const hasAddress = !!matchingAddress;
  const extraFlag: [string, number] | undefined = !hasAddress
    ? ["We couldn't find a building/location number for this interest.", 70]
    : undefined;
  const maxSeverity = extraFlag?.[1] ?? 0;

  const flagText =
    extraFlag?.[0] ?? (hasOverride ? "A user edited this value." : "");

  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity,
    isLightMode,
    isBlank: !matchingAddress,
    isRequiredAndIsBlank: false,
  });

  return (
    <div className={styles.horizontalContainer}>
      <span className={styles.fieldLabel}>{label}</span>
      <Tooltip target={`#${tooltipId}`} position="right" />
      {isEditing ? (
        <EnumEditor
          options={{
            value: matchingAddress
              ? formattedAddress(applyOverridesToAddress(matchingAddress))
              : null,
            editorCallback: (val) => {
              const addressId = formattedAddressToAddress[val]?.address_id;
              return addressId && onEdit(addressId);
            },
          }}
          enumValues={Object.keys(formattedAddressToAddress).concat("None")}
          additionalStyles={{
            ...inputTextStyles,
            height: "26px",
          }}
          disabled={false}
          pt={{ input: { style: { paddingTop: "4px" } } }}
        />
      ) : (
        <div style={{ height: "26px", display: "flex", alignItems: "center" }}>
          <span
            style={labelStyles}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
          >
            {matchingAddress
              ? formattedAddress(applyOverridesToAddress(matchingAddress))
              : ""}
          </span>
        </div>
      )}
    </div>
  );
};

const EditableAIFlaggedField = <
  T extends { [field: string]: string | null },
  F,
>({
  listElement,
  values,
  field,
  label,
  isEditing,
  onEdit,
  enumValues,
}: {
  listElement: OL.OverrideableListElement<T, F>;
  values: T;
  field: keyof T & string;
  label: string;
  isEditing: boolean;
  onEdit: (field: keyof T & string, newVal: string) => void;
  enumValues?: string[];
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const hasOverride =
    listElement.overrides[field] !== undefined &&
    listElement.original !== null &&
    listElement.overrides[field] !== listElement.original[field];

  const maxSeverity = 0;

  const flagText = hasOverride
    ? `A user edited this value. The original value was "${
        listElement.original?.[field] ?? "None"
      }".`
    : "";
  const tooltipId = `tooltip-${uuid()}`;
  const value = values[field];

  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity,
    isLightMode,
    isBlank: !value,
    isRequiredAndIsBlank: false,
  });

  return (
    <div className={styles.horizontalContainer}>
      <span className={styles.fieldLabel}>{label}</span>
      <Tooltip target={`#${tooltipId}`} position="right" />
      {isEditing ? (
        enumValues ? (
          <EnumEditor
            options={{ value, editorCallback: (val) => onEdit(field, val) }}
            enumValues={enumValues}
            additionalStyles={{
              ...inputTextStyles,
              height: "26px",
            }}
            disabled={false}
            pt={{ input: { style: { paddingTop: "4px" } } }}
          />
        ) : (
          <InputText
            style={inputTextStyles}
            width="500px"
            value={value ?? undefined}
            onChange={(e) => onEdit(field, e.target.value)}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
          />
        )
      ) : (
        <div style={{ height: "26px", display: "flex", alignItems: "center" }}>
          <span
            style={labelStyles}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
          >
            {value}
          </span>
        </div>
      )}
    </div>
  );
};

const EditableFlaggedField: React.FC<EditableFlaggedFieldProps> = ({
  rawCompanyInfo,
  safeCompanyInfo,
  field,
  label,
  isEditing,
  setSafeCompanyInfo,
  enumValues,
  placeholder,
  states,
  isMonetary = false,
  isDate = false,
}) => {
  const clientId = useClientId();
  const currentValue =
    field === "additional_named_insureds"
      ? (safeCompanyInfo.additional_named_insureds ?? []).join("\n")
      : (safeCompanyInfo[field]?.toString() ?? "");
  const [textValue, setTextValue] = useState(currentValue);
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const hasOverride = field in rawCompanyInfo.private_overrides;
  const isBlank = currentValue === "";
  const isRequiredAndIsBlank =
    isBlank && !!REQUIRED_FIELDS[clientId]?.includes(field);

  const maxSeverity = maxSeverityForCompanyInfoField(
    rawCompanyInfo,
    field,
    safeCompanyInfo,
    clientId,
    states
  );

  const flagText = isRequiredAndIsBlank
    ? requiredTextForField(clientId, field)
    : hasOverride
      ? `A user edited this value. The original value was "${
          rawCompanyInfo.private_original_values[field] || "None"
        }".`
      : flagTextForCompanyInfoField(
          rawCompanyInfo,
          field,
          safeCompanyInfo,
          clientId,
          states
        );

  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity,
    isLightMode,
    isBlank,
    isRequiredAndIsBlank,
  });

  const tooltipId = `tooltip-${uuid()}`;

  const onEdit = (newValue: string) => {
    setSafeCompanyInfo((oldCompanyInfo) => {
      const newCompanyInfo = { ...oldCompanyInfo };
      if (field === "additional_named_insureds") {
        const newValues = newValue
          .split("\n")
          .map((value) => value.trim())
          .filter((value) => value !== "");

        newCompanyInfo[field] = newValues;
      } else {
        newCompanyInfo[field] = newValue;
      }
      return newCompanyInfo;
    });
  };

  useEffect(() => {
    if (field === "additional_named_insureds") {
      const newValues = textValue
        .split("\n")
        .map((value) => value.trim())
        .filter((value) => value !== "");
      if (!isEqual(newValues, safeCompanyInfo[field])) {
        onEdit(newValues.join("\n"));
      }
    }
  }, [textValue]);

  return (
    <div className={styles.horizontalContainer}>
      {field !== "additional_named_insureds" && (
        <span className={styles.label}>{label}</span>
      )}

      <Tooltip target={`#${tooltipId}`} position="right" />
      {isEditing ? (
        enumValues ? (
          <EnumEditor
            options={{ value: currentValue, editorCallback: onEdit }}
            enumValues={enumValues}
            additionalStyles={{ ...inputTextStyles, height: "26px" }}
            disabled={false}
            pt={{ input: { style: { paddingTop: "4px" } } }}
          />
        ) : field === "additional_named_insureds" ? (
          <InputTextarea
            style={{ ...inputTextStyles, height: "auto", width: "360px" }}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
            rows={4}
            placeholder="Separate with newlines"
          />
        ) : (
          <div
            data-pr-tooltip={flagText}
            data-pr-showdelay={500}
            id={tooltipId}
            style={{ display: "inline-block" }}
          >
            {isMonetary ? (
              <InputNumber
                pt={{ input: { root: { style: inputTextStyles } } }}
                value={
                  Number.isNaN(Number.parseFloat(currentValue))
                    ? undefined
                    : Number.parseFloat(currentValue)
                }
                onChange={(e) => onEdit(e.value?.toString() ?? "")}
                placeholder={placeholder}
                mode="currency"
                currency="USD"
                locale="en-US"
              />
            ) : isDate ? (
              <Calendar
                // This will show blank if the current value is not mm/dd/yyyy
                value={mmddyyyyToDate(currentValue)}
                onChange={(e) => e.value && onEdit(dateToMMDDYYYY(e.value))}
                style={{
                  width: "200px",
                  height: "24px",
                }}
                showButtonBar
                onClearButtonClick={() => onEdit("")}
              />
            ) : (
              <InputText
                style={inputTextStyles}
                value={currentValue}
                onChange={(e) => onEdit(e.target.value)}
                placeholder={placeholder}
              />
            )}
          </div>
        )
      ) : (
        <>
          {field === "additional_named_insureds" ? (
            <div className={styles.verticalContainer}>
              {safeCompanyInfo.additional_named_insureds?.map((name) => (
                <span
                  key={name}
                  style={labelStyles}
                  data-pr-tooltip={flagText}
                  data-pr-showdelay={500}
                  id={tooltipId}
                >
                  {name}
                </span>
              ))}
            </div>
          ) : (
            <div
              style={{ height: "26px", display: "flex", alignItems: "center" }}
            >
              <span
                style={{
                  ...labelStyles,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                data-pr-tooltip={flagText}
                data-pr-showdelay={500}
                id={tooltipId}
              >
                {isMonetary
                  ? formatAbbreviatedMoney(Number(currentValue))
                  : currentValue}
                {!hasOverride &&
                  elementForFlags(
                    rawCompanyInfo,
                    field,
                    safeCompanyInfo,
                    clientId,
                    states
                  )}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EditablePerilSentence = ({
  isEditing,
  policyInfo,
  setPolicyInfo,
}: {
  isEditing: boolean;
  policyInfo: client.PolicyInfo;
  setPolicyInfo: Dispatch<SetStateAction<client.PolicyInfo | null | undefined>>;
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const defaultPerilSentence = perilSentenceFromEnabledPerils(
    policyInfo.included_perils,
    policyInfo.peril_sentence_override
  );
  const hasOverride = !!policyInfo.peril_sentence_override;
  const flagText = hasOverride ? "A user edited this value." : null;
  const { inputTextStyles, labelStyles } = flagStyles({
    hasOverride,
    maxSeverity: 0,
    isLightMode,
    isBlank: false,
    isRequiredAndIsBlank: false,
  });
  return (
    <>
      {isEditing ? (
        <TooltipWrapper tooltipText={flagText ?? ""}>
          <InputTextarea
            style={{
              ...inputTextStyles,
              height: "auto",
              width: "100%",
              marginTop: "8px",
            }}
            value={defaultPerilSentence}
            onChange={(e) =>
              setPolicyInfo((oldInfo) => {
                const newInfo = cloneDeep(oldInfo) || {
                  included_perils: [],
                  selected_policy_form_name: null,
                };
                newInfo.peril_sentence_override = e.target.value;
                return newInfo;
              })
            }
          />
        </TooltipWrapper>
      ) : (
        <TooltipWrapper tooltipText={flagText ?? ""}>
          <div style={{ ...labelStyles, marginTop: "8px" }}>
            {defaultPerilSentence}
          </div>
        </TooltipWrapper>
      )}
    </>
  );
};

type GenearlInputProps = {
  specialtyPropertyInfo: client.ReportResponse;
};

export const GeneralInput: React.FC<GenearlInputProps> = ({
  specialtyPropertyInfo,
}) => {
  const report = specialtyPropertyInfo.report_json;
  const clientId = useClientId();
  const addresses = useMemo(
    () =>
      report.sovs
        .filter((sov) => sov.is_enabled)
        .flatMap((sov) => sov.addresses),
    [report]
  );
  const rawCompanyInfo = useMemo(
    () => report.company_info,
    [specialtyPropertyInfo]
  );
  const originalSafeCompanyInfo = useMemo(
    () => applyOverridesToCompanyInfo(rawCompanyInfo, clientId),
    [rawCompanyInfo]
  );
  const originalPolicyInfo = useMemo(
    () => report.policy_info,
    [specialtyPropertyInfo]
  );
  const [isEditing, setIsEditing] = useState(false);
  const [safeCompanyInfo, setSafeCompanyInfo] = useState(
    cloneDeep(originalSafeCompanyInfo)
  );
  const [policyInfo, setPolicyInfo] = useState(cloneDeep(report.policy_info));
  const setValuationType = (
    field: keyof client.ValuationInfo,
    newValue: string | null
  ) => {
    setPolicyInfo((old) => {
      const info = cloneDeep(old) ?? {
        included_perils: [],
        selected_policy_form_name: null,
      };
      info.valuation_info = {
        physical_valuation_type_1: null,
        physical_valuation_type_2: null,
        bi_valuation_type: null,
        equipment_valuation_type: null,
        ...info.valuation_info,
      };
      info.valuation_info[field] = newValue;
      return info;
    });
  };
  const setReinsurerPercentage = (
    field: keyof client.ReinsurerInfo,
    newValue: number
  ) => {
    setPolicyInfo((old) => {
      const info = cloneDeep(old) ?? {
        included_perils: [],
        selected_policy_form_name: null,
      };
      info.reinsurer_info = {
        ...DEFAULT_REINSURER_INFO,
        ...info.reinsurer_info,
      };
      info.reinsurer_info[field] = newValue;
      return info;
    });
  };
  const effectiveReinsurerInfo =
    policyInfo?.reinsurer_info ?? DEFAULT_REINSURER_INFO;
  const reinsurerTotal =
    effectiveReinsurerInfo.ren_percentage +
    effectiveReinsurerInfo.nephila_percentage +
    effectiveReinsurerInfo.starr_percentage;
  const policyDropdownOptions = getSelectablePolicyFormNames(
    report.attachment_info,
    report.deductible_info,
    report.limit_info
  ).map((name) => ({ code: name, label: name }));
  const [additionalInterests, setAdditionalInterests] = useState(
    rawCompanyInfo.additional_interests ?? []
  );
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  useEffect(() => {
    setSafeCompanyInfo(originalSafeCompanyInfo);
  }, [originalSafeCompanyInfo]);

  return (
    <>
      <Card
        header={
          <div
            className={styles.horizontalContainer}
            style={{
              paddingTop: "20px",
              paddingLeft: "20px",
            }}
          >
            <span
              className={`p-card-title ${styles.sectionTitle}`}
              style={{ marginRight: "12px" }}
            >
              General
            </span>
            {!isEditing && (
              <>
                <Button
                  disabled={updateReport.isPending}
                  label="Edit"
                  onClick={() => setIsEditing(true)}
                  icon="pi pi-pencil"
                  raised
                  severity="secondary"
                  text
                />
              </>
            )}
            {isEditing && (
              <>
                <Button
                  label="Cancel"
                  disabled={updateReport.isPending}
                  onClick={() => {
                    setSafeCompanyInfo(originalSafeCompanyInfo);
                    setPolicyInfo(originalPolicyInfo);
                    setAdditionalInterests(
                      rawCompanyInfo.additional_interests ?? []
                    );
                    setIsEditing(false);
                  }}
                  raised
                  severity="secondary"
                  text
                />
                <Button
                  label={"Save"}
                  loading={updateReport.isPending}
                  disabled={
                    isEqual(originalSafeCompanyInfo, safeCompanyInfo) &&
                    isEqual(
                      additionalInterests,
                      rawCompanyInfo.additional_interests
                    ) &&
                    isEqual(originalPolicyInfo, policyInfo)
                  }
                  onClick={async () => {
                    const allKeys = Array.from(
                      Object.keys(safeCompanyInfo)
                    ) as (keyof client.RawCompanyInfo)[];
                    const newCompanyInfo = cloneDeep(rawCompanyInfo);
                    for (const key of allKeys) {
                      const newValue = safeCompanyInfo[key];

                      if (
                        newValue !== undefined &&
                        newValue !== null &&
                        !isEqual(newValue, originalSafeCompanyInfo[key])
                      ) {
                        newCompanyInfo.private_overrides[key] = newValue;
                      }

                      // Drop fields that are the same as their original values
                      if (
                        isEqual(
                          newValue,
                          newCompanyInfo.private_computed_values[key]
                        )
                      ) {
                        delete newCompanyInfo.private_overrides[key];
                      }
                    }
                    newCompanyInfo.additional_interests = additionalInterests;

                    // Track which policy_info fields the user has edited
                    if (policyInfo) {
                      type PolicyDataKey = keyof Omit<
                        client.PolicyInfo,
                        "edited_fields"
                      >;
                      const policyFieldKeys = Object.keys(policyInfo).filter(
                        (k): k is PolicyDataKey => k !== "edited_fields"
                      );
                      const newEditedFields = new Set(
                        policyInfo.edited_fields ?? []
                      );
                      for (const key of policyFieldKeys) {
                        if (
                          !isEqual(policyInfo[key], originalPolicyInfo?.[key])
                        ) {
                          newEditedFields.add(key);
                        }
                      }
                      policyInfo.edited_fields = Array.from(newEditedFields);
                    }

                    await updateReport.mutateAsync({
                      companyInfo: newCompanyInfo,
                      policyInfo: policyInfo ?? undefined,
                    });
                    setIsEditing(false);
                  }}
                  raised
                  text
                />
              </>
            )}
          </div>
        }
      >
        <div className={styles.gridContainer} style={{ marginTop: "-20px" }}>
          <div className={styles.fieldGroup}>
            <span className={styles.header} style={{ marginTop: "0px" }}>
              GENERAL
            </span>
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Named Insured"
              field="named_insured"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="DBA"
              field="dba"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Effective Date"
              field="effective_date"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              placeholder="mm/dd/yyyy"
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
              isDate
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Expiration Date"
              field="expiration_date"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              placeholder="mm/dd/yyyy"
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
              isDate
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Files Received Date"
              field="files_received_date"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              placeholder="mm/dd/yyyy"
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
              isDate
            />
            <IfFeatureEnabled feature="show-target-premium-in-general-tab">
              <EditableFlaggedField
                rawCompanyInfo={rawCompanyInfo}
                safeCompanyInfo={safeCompanyInfo}
                label="Target Premium"
                field="target_premium"
                isEditing={isEditing}
                setSafeCompanyInfo={setSafeCompanyInfo}
                isMonetary={true}
                states={
                  specialtyPropertyInfo.precomputed_metadata
                    .unique_location_states ?? []
                }
              />
            </IfFeatureEnabled>
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Business Description"
              field="business_description"
              enumValues={BUSINESS_DESCRIPTIONS}
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Entity Type"
              field="entity_type"
              enumValues={[
                "LLC",
                "Corporation",
                "Joint Venture",
                "Parternship",
                "Individual",
              ]}
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Years in Business"
              field="years_in_business"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.header}>INSURED MAILING ADDRESS</span>
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Mailing Street Address"
              field="company_address"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Mailing City"
              field="company_city"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Mailing State"
              field="company_state"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label="Mailing Zip Code"
              field="company_zip"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
          </div>
          {policyInfo && (
            <div className={styles.fieldGroup}>
              <span className={styles.header}>FORMS AND PERILS</span>
              <span className={styles.labelWide}>
                Selected Policy Form / Specs Sheet
              </span>
              <Dropdown
                disabled={!isEditing}
                options={policyDropdownOptions}
                value={policyDropdownOptions.find(
                  (option) =>
                    option.code === policyInfo?.selected_policy_form_name
                )}
                onChange={(e) => {
                  setPolicyInfo((oldPolicyInfo) => {
                    const newPolicyInfo = cloneDeep(oldPolicyInfo) ?? {
                      included_perils: [],
                      selected_policy_form_name: null,
                    };
                    newPolicyInfo.selected_policy_form_name = e.value.code;
                    return newPolicyInfo;
                  });
                }}
                optionLabel="label"
                style={{ maxWidth: "440px" }}
              />

              <FormSelectionDropdown
                value={policyInfo.form_selection ?? null}
                disabled={!isEditing}
                onChange={(value) =>
                  setPolicyInfo((old) => {
                    const info = cloneDeep(old) ?? {
                      included_perils: [],
                      selected_policy_form_name: null,
                    };
                    info.form_selection = value;
                    return info;
                  })
                }
              />
              <BrokerCommissionInput
                value={policyInfo.broker_commission_percentage ?? null}
                disabled={!isEditing}
                onChange={(value) =>
                  setPolicyInfo((old) => {
                    const info = cloneDeep(old) ?? {
                      included_perils: [],
                      selected_policy_form_name: null,
                    };
                    info.broker_commission_percentage = value;
                    return info;
                  })
                }
              />
              <Divider />
              <PerilCheckboxes
                value={policyInfo.included_perils}
                disabled={!isEditing}
                onChange={(perils) =>
                  setPolicyInfo((old) => {
                    const info = cloneDeep(old) ?? {
                      included_perils: [],
                      selected_policy_form_name: null,
                    };
                    info.included_perils = perils;
                    return info;
                  })
                }
              />
              <EditablePerilSentence
                policyInfo={policyInfo}
                setPolicyInfo={setPolicyInfo}
                isEditing={isEditing}
              />
              <Divider />
              <span className={styles.labelWide}>Surge Included With</span>
              <Dropdown
                disabled={
                  !isEditing || policyInfo.surge_association === "pinned"
                }
                options={SURGE_ASSOCIATION_OPTIONS}
                optionLabel="label"
                optionValue="value"
                optionDisabled={(option) => option.value === "pinned"}
                value={policyInfo.surge_association ?? "wind"}
                onChange={(e) => {
                  setPolicyInfo((old) => {
                    const info = cloneDeep(old) ?? {
                      included_perils: [],
                      selected_policy_form_name: null,
                    };
                    info.surge_association = e.value;
                    return info;
                  });
                }}
                tooltip={
                  policyInfo.surge_association === "pinned"
                    ? "Pinned to wind-only named storm pricing (modeled before wind + surge support)"
                    : undefined
                }
                tooltipOptions={{ showOnDisabled: true }}
                style={{ maxWidth: "440px" }}
              />
            </div>
          )}
          {policyInfo && (
            <div className={styles.fieldGroup}>
              <span className={styles.header}>VALUATION TYPES</span>
              <ValuationTypeField
                label="Physical (Buildings, BPP, Contents, etc.)"
                options={PHYSICAL_VALUATION_TYPE_1_OPTIONS}
                value={policyInfo.valuation_info?.physical_valuation_type_1}
                disabled={!isEditing}
                onChange={(v) =>
                  setValuationType("physical_valuation_type_1", v)
                }
              />
              <ValuationTypeField
                label="Stock / Inventory (Stock, Inventory, Outdoor, etc.)"
                options={PHYSICAL_VALUATION_TYPE_2_OPTIONS}
                value={policyInfo.valuation_info?.physical_valuation_type_2}
                disabled={!isEditing}
                onChange={(v) =>
                  setValuationType("physical_valuation_type_2", v)
                }
              />
              <ValuationTypeField
                label="Business Income / Extra Expense"
                options={BI_VALUATION_TYPE_OPTIONS}
                value={policyInfo.valuation_info?.bi_valuation_type}
                disabled={!isEditing}
                onChange={(v) => setValuationType("bi_valuation_type", v)}
              />
              <ValuationTypeField
                label="Equipment (Contractors, Mobile)"
                options={EQUIPMENT_VALUATION_TYPE_OPTIONS}
                value={policyInfo.valuation_info?.equipment_valuation_type}
                disabled={!isEditing}
                onChange={(v) =>
                  setValuationType("equipment_valuation_type", v)
                }
              />
            </div>
          )}
          {policyInfo && (
            <div className={styles.fieldGroup}>
              <span className={styles.header}>REINSURERS</span>
              <ReinsurerPercentageField
                label="RenaissanceRe"
                value={effectiveReinsurerInfo.ren_percentage}
                disabled={!isEditing}
                onChange={(v) => setReinsurerPercentage("ren_percentage", v)}
              />
              <ReinsurerPercentageField
                label="Nephila"
                value={effectiveReinsurerInfo.nephila_percentage}
                disabled={!isEditing}
                onChange={(v) =>
                  setReinsurerPercentage("nephila_percentage", v)
                }
              />
              <ReinsurerPercentageField
                label="Starr"
                value={effectiveReinsurerInfo.starr_percentage}
                disabled={!isEditing}
                onChange={(v) => setReinsurerPercentage("starr_percentage", v)}
              />
              {Math.abs(reinsurerTotal - 100) > 0.01 && (
                <span className={styles.percentageWarning}>
                  Reinsurer percentages total {reinsurerTotal}%, not 100%.
                </span>
              )}
            </div>
          )}
          <div className={styles.fieldGroup}>
            <span className={styles.header}>ADDITIONAL NAMED INSUREDS</span>
            <EditableFlaggedField
              rawCompanyInfo={rawCompanyInfo}
              safeCompanyInfo={safeCompanyInfo}
              label=""
              field="additional_named_insureds"
              isEditing={isEditing}
              setSafeCompanyInfo={setSafeCompanyInfo}
              states={
                specialtyPropertyInfo.precomputed_metadata
                  .unique_location_states ?? []
              }
            />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.header}>ADDITIONAL INTERESTS</span>
            <EditableAdditionalInterests
              additionalInterests={additionalInterests}
              setAdditionalInterests={setAdditionalInterests}
              isEditing={isEditing}
              allAddresses={addresses}
            />
          </div>
        </div>
      </Card>
    </>
  );
};
